import { makeAutoObservable } from 'mobx'
import { nanoid } from 'nanoid'
import type { IDropdownItem, IIconProps } from 'shared/ui'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { type ISmartCallFlowIVRStep } from 'entities/SmartCall/api/types'
import { FlowActionStore } from 'widgets/Flow'
import {
  generateSmartCallActionId,
  type ISmartCallActionStore,
  type ISmartCallFlowActionStoreProps,
} from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/Action'
import {
  type ISmartCallFlowAction,
  type ISmartCallFlowActionType,
} from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/type'
import { GreetingAudioBlockStore } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/blocks/GreetingAudioBlock'
import { type SmartCallFlowBuilderStore } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder'

export type IIVRStoreProps = {
  id?: ISmartCallFlowActionStoreProps['id']
  parentId: ISmartCallFlowActionStoreProps['parentId']
  args?: ISmartCallFlowIVRStep['args']
}

export type IBrunch = { id: string; key: number | 'no_selection'; title: string; nextId: string }
const brunchesKeys = [1, 2, 3, 4, 5, 6, 7, 8, 9, 0]

export class IVRStore {
  static readonly type = 'ivrPhoneTree' satisfies ISmartCallFlowActionType
  static title = 'IVR phone tree'
  static iconProps: IIconProps = {
    icon: 'dataflow',
    color: 'var(--action-contained-primary-normal)',
  }
  public readonly actionStore: ISmartCallActionStore
  brunchLabel = 'No selection'
  readonly greetingMessageStore: GreetingAudioBlockStore

  constructor(
    private _entities: IEntitiesStore,
    { id, args, ...props }: IIVRStoreProps,
    private parent: SmartCallFlowBuilderStore
  ) {
    this.actionStore = new FlowActionStore({
      ...props,
      id: id || generateSmartCallActionId(`${this._stepIdType.type}_${this._stepIdType.id}`),
      type: IVRStore.type,
      name: IVRStore.title,
      allowedNextActions: [
        'callingFlow',
        'forwardCall',
        'playSound',
        'sendVoicemail',
        'aiCallingAgent',
        'callQueue',
      ],
      allowedBeforeActions: [],
      edgeType: 'customEdgeWithLabel',
    })
    this.greetingMessageStore = new GreetingAudioBlockStore(
      this._entities,
      this.parent.parent,
      'Your message is the key – please select an audio',
      args?.greeting_audio_url
        ? {
            audio_url: args?.greeting_audio_url,
          }
        : undefined
    )

    if (args) {
      args.menu_options?.forEach((option) => {
        if (option.key === 'no_selection') return
        const branch: IBrunch = {
          id: nanoid(),
          key: option.key,
          nextId: option.stepId,
          title: option.name,
        }
        this._brunchesMap.set(branch.id, branch)
      })
    }

    makeAutoObservable(this)
  }

  private _stepIdType: Pick<ISmartCallFlowIVRStep, 'id' | 'type'> = {
    id: IVRStore.type,
    type: 'action',
  }

  get no_selection_branch(): ISmartCallFlowIVRStep['args']['menu_options'][number] {
    return {
      key: 'no_selection',
      name: 'No Selection',
      stepId: this.actionStore.nextIds.at(-1) || '',
    }
  }

  get step(): ISmartCallFlowIVRStep {
    return {
      ...this._stepIdType,
      args: {
        greeting_audio_url: this.greetingMessageStore.args.audio_url,
        menu_options: [
          ...this.brunches.map((brunch) => ({
            stepId: brunch.nextId,
            key: brunch.key,
            name: brunch.title,
          })),
          this.no_selection_branch,
        ],
      },
      next: this.no_selection_branch.stepId,
    }
  }

  setApiError = (key: string, message: string) => {}

  validate = () => {
    let valid = true
    if (!this.greetingMessageStore.validate()) {
      valid = false
    }
    if (!this.brunches.length) {
      this.setBrunchesError('Add a keypad options to continue')
      valid = false
    }
    return valid
  }

  get isConfigurationError() {
    return !!this.greetingMessageStore.error || !!this.brunchesError
  }

  _brunchesMap: Map<string, IBrunch> = new Map()
  get brunches() {
    return Array.from(this._brunchesMap.values())
  }
  private _brunchesError = ''
  get brunchesError() {
    return this._brunchesError
  }
  setBrunchesError = (value: string) => {
    this._brunchesError = value
  }

  get selectedBrunchesKeys() {
    return this.brunches.map((item) => item.key)
  }
  addBrunch = () => {
    this.setBrunchesError('')
    const ringAction = this.parent.createAction('callingFlow', {
      parentId: this.actionStore.id,
    })
    if (ringAction) {
      this.actionStore.addNextIdBefore(ringAction.actionStore.id)
      this.parent.addAction(ringAction)
      this.parent.appendCallEndsWithAddButton(ringAction.actionStore)

      const sendToVoicemailAction: ISmartCallFlowAction | null = this.parent.createAction(
        'sendVoicemail',
        {
          parentId: ringAction.actionStore.id,
        }
      )
      if (sendToVoicemailAction) {
        ringAction.actionStore.addNextId(sendToVoicemailAction.actionStore.id)
        this.parent.addAction(sendToVoicemailAction)
        this.parent.appendCallEndsWithAddButton(sendToVoicemailAction.actionStore)
      }
    }

    if (ringAction) {
      const key = brunchesKeys.find((key) => !this.selectedBrunchesKeys.includes(key))
      if (key === undefined) return
      const newBrunch: IBrunch = {
        id: nanoid(),
        key,
        title: '',
        nextId: ringAction?.actionStore.id,
      }
      this._brunchesMap.set(newBrunch.id, newBrunch)
    }
  }
  get disabledAddBrunch() {
    return this.selectedBrunchesKeys.length === brunchesKeys.length
  }
  updateBrunch = (brunch: IBrunch) => {
    this._brunchesMap.set(brunch.id, brunch)
  }
  deleteBrunch = (brunch: IBrunch) => {
    this._brunchesMap.delete(brunch.id)
    this.actionStore.removeNextId(brunch.nextId)
    const action = this.parent.getActionById(brunch.nextId)
    if (action) this.parent.removeActionAndAllChildren(action)
  }
  getBranchNameByTargetId = (id: string) => {
    const data = this.brunches.find((b) => b.nextId === id)
    if (data) {
      return `Key ${data.key}${data.title ? ` → ${data.title}` : ''}`
    }
    return 'No selection'
  }

  getBrunchKeyOptions = (brunch: IBrunch): IDropdownItem[] => {
    return brunchesKeys
      .filter((key) => brunch.key === key || !this.selectedBrunchesKeys.includes(key))
      .map((key) => ({
        id: key,
        label: String(key),
      }))
  }
}
